import {notify} from "@client/features/notifications";

import * as utils from "@client/utils";

import type {Action, AppDispatch, RootState} from "@client/store";
import {APICredentials, Metrics, Schema, models, server} from "@reactivated";

export type Actions =
    | {
          type: "SET_PENDING_REGISTRATION";
          pendingRegistration:
              | models["journal.day.Method"]
              | models["core.rpc.MFPRegistration"]
              | null;
      }
    | {
          type: "FETCH_SUBSCRIPTION_SUCCESS";
          subscription: models["core.payments.Subscription"] | null;
      }
    | {
          type: "SET_HEALTHKIT_METRICS";
          metrics: Metrics;
      }
    | {
          type: "SET_HEALTHKIT_WORKOUTS";
          workouts: Record<DateValue, server.journal.day.WorkoutAggregate.input[]>;
      }
    | {
          type: "CREATE_SUBSCRIPTION_SUCCESS";
      }
    | {
          type: "DELETE_SUBSCRIPTION_SUCCESS";
      }
    | {
          type: "FETCH_CREDENTIALS_SUCCESS";
          credentials: APICredentials;
      }
    | {
          type: "SET_PROFILE";
          profile: server.core.rpc.Profile.output;
      }
    | {
          type: "SET_HEALTH_NEEDS_UPDATE";
          value: boolean;
      }
    | {
          type: "CLEAR_SHARE";
      }
    | {
          type: "SHARE_SUCCESS";
          share: {
              type: Schema["rpc_share_detail_output"]["type"];
              share_uuid: string;
          };
      };

export const fetchSubscription = () => async (dispatch: AppDispatch) => {
    const result = await server.core.payments.subscription_detail();

    if (result.type == "success") {
        dispatch({
            type: "FETCH_SUBSCRIPTION_SUCCESS",
            subscription: result.data,
        });
    }
};

export const createSubscription =
    ({token}: {token: string}) =>
    async (dispatch: AppDispatch) => {
        const result = await server.core.payments.create_subscription({token});

        if (result.type == "success") {
            const subscriptionOrErrorMessage = result.data;

            if (typeof subscriptionOrErrorMessage == "string") {
                dispatch(
                    notify(subscriptionOrErrorMessage, {
                        type: "error",
                    }),
                );
            } else {
                dispatch({
                    type: "FETCH_SUBSCRIPTION_SUCCESS",
                    subscription: subscriptionOrErrorMessage,
                });
            }
        } else {
            dispatch(
                notify("There was an error. We're looking into this.", {
                    type: "error",
                }),
            );
            throw new Error("Unxpected error when subscribing");
        }
    };

export const deleteSubscription = () => async (dispatch: AppDispatch) => {
    const result = await server.core.payments.delete_subscription();

    if (result.type == "success") {
        dispatch({
            type: "DELETE_SUBSCRIPTION_SUCCESS",
        });
    }
};

export const getSubscription = (state: RootState) => {
    return state.orm.subscription;
};

const initialState = {
    pendingRegistration: null as
        | models["journal.day.Method"]
        | models["core.rpc.MFPRegistration"]
        | null,
    profile: null as server.core.rpc.Profile.output | null,
    journal: {} as Record<DateValue, server.journal.day.Journal.output | null>,
    apiCredentials: null as APICredentials | null,
    subscription: null as models["core.payments.Subscription"] | null,
    // TODO: needs better typing.
    metrics: {} as Record<
        string,
        Record<string, server.journal.day.MetricAggregate.input | null> | null
    >,
    workouts: {} as Record<
        DateValue,
        server.journal.day.WorkoutAggregate.input[] | null
    >,
    // Memory-only, never persisted: iOS re-reports it on every sync, and writing
    // it into the device's apple_health setting is what used to stop syncs.
    healthNeedsUpdate: false,
};

export const reducer = (state = initialState, action: Action): typeof initialState => {
    switch (action.type) {
        case "LOGOUT": {
            // Reset all our objects except API credentials.
            return {...initialState, apiCredentials: state.apiCredentials};
        }
        case "SET_PENDING_REGISTRATION": {
            return {
                ...state,
                pendingRegistration: action.pendingRegistration,
            };
        }
        case "SET_HEALTHKIT_METRICS": {
            return {...state, metrics: {...state.metrics, ...action.metrics}};
        }
        case "SET_HEALTHKIT_WORKOUTS": {
            return {...state, workouts: {...state.workouts, ...action.workouts}};
        }
        case "FETCH_SUBSCRIPTION_SUCCESS": {
            return utils.assign(state, {
                subscription: action.subscription,
            });
        }
        case "DELETE_SUBSCRIPTION_SUCCESS": {
            return utils.assign(state, {
                subscription: null as Schema["rpc_subscription_detail_output"] | null,
            });
        }
        case "FETCH_CREDENTIALS_SUCCESS": {
            return {
                ...state,
                apiCredentials: action.credentials,
            };
        }
        case "SET_PROFILE": {
            return {
                ...state,
                profile: action.profile,
            };
        }
        case "SET_HEALTH_NEEDS_UPDATE": {
            return {
                ...state,
                healthNeedsUpdate: action.value,
            };
        }
        default: {
            return state;
        }
    }
};

export function getMetrics(
    state: RootState,
    journal: server.journal.day.Journal.output,
) {
    const WATER =
        journal.metrics.WATER?.is_manual == true ? journal.metrics.WATER.value : null;

    const SLEEP =
        journal.metrics.SLEEP?.is_manual == true ? journal.metrics.SLEEP.value : null;

    const STEPS =
        journal.metrics.STEPS?.is_manual == true ? journal.metrics.STEPS.value : null;

    const WEIGHT =
        journal.metrics.WEIGHT?.is_manual == true ? journal.metrics.WEIGHT.value : null;

    return {
        ACTIVE_CALORIES:
            state.orm.metrics[journal.day.key]?.ACTIVE_CALORIES?.value ??
            journal.metrics.ACTIVE_CALORIES?.value,
        BASE_CALORIES:
            state.orm.metrics[journal.day.key]?.BASE_CALORIES?.value ??
            journal.metrics.BASE_CALORIES?.value,
        SLEEP:
            SLEEP ??
            state.orm.metrics[journal.day.key]?.SLEEP?.value ??
            journal.metrics.SLEEP?.value,
        STEPS:
            STEPS ??
            state.orm.metrics[journal.day.key]?.STEPS?.value ??
            journal.metrics.STEPS?.value,
        WEIGHT:
            WEIGHT ??
            state.orm.metrics[journal.day.key]?.WEIGHT?.value ??
            journal.metrics.WEIGHT?.value,
        WATER,
    };
}

export function getActivities(
    state: RootState,
    journal: server.journal.day.Journal.output,
): server.journal.day.WorkoutAggregate.input[] {
    return state.orm.workouts[journal.day.key] ?? journal.activities;
}

export const fetchProfile = () => async (dispatch: AppDispatch) => {
    const result = await server.core.rpc.profile_detail();

    if (result.type != "success") {
        throw new Error("Error loading profile");
    }
    dispatch({
        type: "SET_PROFILE",
        profile: result.data,
    });
};

export const dismissAnnouncement =
    (announcement: models["core.constants.Announcement"]) =>
    async (dispatch: AppDispatch) => {
        const result = await server.core.rpc.dismiss_announcement({
            announcement,
        });

        if (result.type != "success") {
            throw new Error("Error dismissing announcement");
        }
        void dispatch(fetchProfile());
    };

export const getProfile = (state: RootState) => {
    return state.orm.profile;
};

export const getHealthNeedsUpdate = (state: RootState) => state.orm.healthNeedsUpdate;

export const getCredentials = (state: RootState) => state.orm.apiCredentials;
